import React, { useState, useEffect } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import "../styles/AdminLayout.css";
import { motion, AnimatePresence } from "framer-motion";
import { Toaster } from "react-hot-toast"; // Notification container
import {
  LayoutDashboard,
  Users,
  LogOut,
  ShieldCheck,
  MessageSquare,
  Menu,
  X,
  ClipboardList,
  FileSearch ,
  Contact 
} from "lucide-react";

const Admin = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [userEmail, setUserEmail] = useState("Admin User");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const getUserData = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.email) setUserEmail(user.email);
    };
    getUserData();
  }, []);

  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/login");
  };

  return (
    <div className="rni-portal-wrapper">
      {/* Toast notifications will appear here */}
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#ffffff',
            color: '#0f172a',
            fontFamily: 'Inter, sans-serif',
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
          },
        }} 
      />

      <header className="rni-portal-mobile-bar">
        <img src="https://res.cloudinary.com/dxgkcyfrl/image/upload/v1767593282/logo1_ssbtzf.png" alt="Rift Logo" className="rni-portal-mobile-logo" />
        <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className="rni-portal-burger">
          {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </header>

      <AnimatePresence>
        {isSidebarOpen && (
          <motion.div className="rni-portal-overlay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setIsSidebarOpen(false)} />
        )}
      </AnimatePresence>

      <aside className={`rni-portal-sidebar ${isSidebarOpen ? "rni-portal-active" : ""}`}>
        <div className="rni-portal-branding">
          <img src="https://res.cloudinary.com/dxgkcyfrl/image/upload/v1767593282/logo1_ssbtzf.png" alt="Logo" className="rni-portal-logo-img" />
          <div className="rni-portal-brand-meta">
            <span>Rift Insurance</span>
            <small>Admin Portal</small>
          </div>
        </div>

        <div className="rni-portal-user-section">
          <div className="rni-portal-avatar"><ShieldCheck size={20} color="#1e5bb8" /></div>
          <div className="rni-portal-user-info">
            <p className="rni-user-name">Authorized Admin</p>
            <p className="rni-user-email">{userEmail}</p>
          </div>
        </div>

        <nav className="rni-portal-nav">
          <div className="rni-nav-label">Main Menu</div>
          
          <Link to="/admin/dashboard" className={`rni-portal-nav-link ${location.pathname === "/admin/dashboard" ? "rni-is-active" : ""}`}>
            <LayoutDashboard size={20} /> <span>Dashboard</span>
          </Link>

          <Link to="/admin/claims" className={`rni-portal-nav-link ${location.pathname === "/admin/claims" ? "rni-is-active" : ""}`}>
            <ClipboardList size={20} /> <span>Incident Claims</span>
          </Link>

          <Link to="/admin/quotes" className={`rni-portal-nav-link ${location.pathname === "/admin/quotes" ? "rni-is-active" : ""}`}>
            <FileSearch size={20} /> <span>Quote Requests</span>
          </Link>

          <Link to="/admin/roster" className={`rni-portal-nav-link ${location.pathname === "/admin/roster" ? "rni-is-active" : ""}`}>
            <Users size={20} /> <span>Agent Roster</span>
          </Link>

          <Link to="/admin/testimonials" className={`rni-portal-nav-link ${location.pathname === "/admin/testimonials" ? "rni-is-active" : ""}`}>
            <MessageSquare size={20} /> <span>Wall of Love</span>
          </Link>
          <Link to="/admin/contact" className={`rni-portal-nav-link ${location.pathname === "/admin/contact" ? "rni-is-active" : ""}`}>
            <Contact  size={20} /> <span>Contact Inquiries</span>
          </Link>
        </nav>

        <div className="rni-portal-sidebar-footer">
          <button onClick={handleLogout} className="rni-portal-logout">
            <LogOut size={18} /> <span>Logout</span>
          </button>
        </div>
      </aside>

      <main className="rni-portal-main">
        <motion.div key={location.pathname} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="rni-portal-view">
          <Outlet />
        </motion.div>
      </main>
    </div>
  );
};

export default Admin;